/// <amd-module name="Feed/Legacy/Components/Settings/AddUpstreamPanel" />
import * as React from "react";

import { DefaultButton, PrimaryButton } from "OfficeFabric/Button";
import { Overlay } from "OfficeFabric/Overlay";
import { Panel, PanelType } from "OfficeFabric/Panel";

import { IVssPageContext } from "VSS/Platform/Context";

import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

import { UpstreamSource, UpstreamSourceType } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { FeedSettingsActionCreator } from "Feed/Legacy/Actions/FeedSettingsActionCreator";
import { AddAadInternalSourcePanelSection } from "Feed/Legacy/Components/Settings/AddAadInternalSourcePanelSection";
import { AddCollectionInternalSourcePanelSection } from "Feed/Legacy/Components/Settings/AddCollectionInternalSourcePanelSection";
import { AddPublicSourcePanelSection } from "Feed/Legacy/Components/Settings/AddPublicSourcePanelSection";
import { AddUpstreamPanelStage } from "Feed/Legacy/Components/Settings/CommonTypes";
import { IInternalUpstreamSettingsState } from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import { UpstreamTypeSelectionPanelSection } from "Feed/Legacy/Components/Settings/UpstreamTypeSelectionPanelSection";
import { filterDefaultUpstreamSources } from "Feed/Legacy/Helpers/UpstreamHelper";
import { NpmKey } from "Feed/Legacy/Protocols/Npm/Constants/NpmConstants";
import { ExtendedUpstreamSource, Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";
import { IServiceEndpointDataService } from "Feed/Legacy/DataServices/ServiceEndpointDataService";
import * as ServiceEndpoint from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { getCurrentSelectedProjectId } from "Feed/Legacy/Helpers/ProjectHelper";

export interface IAddUpstreamPanelProps {
    pageContext: IVssPageContext;
    /**
     * Error message to display on upstream source name field
     */
    upstreamSourceNameInvalidMessage?: string;

    /**
     * Error message to display on upstream source location field
     */
    upstreamSourceLocationInvalidMessage?: string;

    /**
     * List of available feeds for upstreams in the collection
     */
    availableFeedsForUpstreams: Feed[];

    /**
     * Currently selected feed for which upstreams are being configured
     */
    currentFeed: Feed;

    /**
     * List of active upstream sources for the selected feed
     */
    activeUpstreamSources: UpstreamSource[];

    /**
     * Internal upstream feature availability
     */
    internalUpstreamSettings: IInternalUpstreamSettingsState;

    /**
     * Tracks if views are getting saved to server
     */
    isSavingChanges: boolean;

    /**
     * While saving, captures errors if they occur to show in UI
     */
    error: Error;

    /**
     * Feature flag for custom upstream sources
     */
    isCustomPublicUpstreamsFeatureEnabled: boolean;

    /**
     * Feature flag for Upstreams with Service Endpoints
     */
    isServiceEndpointUpstreamsFeatureEnabled: boolean;
}

export interface IAddUpstreamPanelState {
    selectedUpstreamSources: ExtendedUpstreamSource[];
    stage: AddUpstreamPanelStage;
    serviceEndpoints: ServiceEndpoint.ServiceEndpoint[];
}

/**
 * Panel that allows the user to specify an upstream source for addition to the feed
 */
export class AddUpstreamPanel extends React.Component<IAddUpstreamPanelProps, IAddUpstreamPanelState> {
    constructor(props: IAddUpstreamPanelProps) {
        super(props);
        const stage = this._getDisplayOnlyPublicUpstream(props)
            ? AddUpstreamPanelStage.PublicUpstream
            : AddUpstreamPanelStage.Initial;

        this.state = {
            selectedUpstreamSources: null,
            stage,
            serviceEndpoints: []
        };

        this._getServiceEndpoints().then(endpoints => {
            this.setState({ serviceEndpoints: endpoints });
        });

    }

    public render(): JSX.Element {
        return (
            <Panel
                isOpen={true}
                onDismiss={this._onDismiss}
                type={PanelType.medium}
                isFooterAtBottom={true}
                onRenderFooterContent={this._onRenderFooterContent}
                headerText={PackageResources.AddUpstreamPanel_Header}
                closeButtonAriaLabel={PackageResources.AriaLabel_ClosePanel}
                hasCloseButton={this.props.isSavingChanges === false}
            >
                {this.props.error != null && (
                    <MessageBar severity={MessageBarSeverity.Error}>{this.props.error.message}</MessageBar>
                )}
                {// The selected subcontrol manually sets focus to itself in its ComponentDidMount,
                    // since Panel.focusTrapZoneProps.firstFocusableSelector code is not rexecuted when
                    // this._stageHandler updates the subcomponent to render.
                    this._getControlsAccordingToStage()}
                {this.props.isSavingChanges && (
                    <Overlay className="feed-overlay">
                        <div className="content">
                            <Spinner
                                size={SpinnerSize.small}
                                label={PackageResources.FeedSettings_Overlay_SavingChanges}
                            />
                        </div>
                    </Overlay>
                )}
            </Panel>
        );
    }

    private async _getServiceEndpoints(): Promise<ServiceEndpoint.ServiceEndpoint[]> {
        let endpoints: ServiceEndpoint.ServiceEndpoint[];
        if(this.props.isServiceEndpointUpstreamsFeatureEnabled) {
            const projectId = this.props.currentFeed.project != null
                ? this.props.currentFeed.project.id
                : getCurrentSelectedProjectId(this.props.pageContext);
            const serviceEndpointService = this.props.pageContext.getService<IServiceEndpointDataService>("IServiceEndpointDataService");
            endpoints = await serviceEndpointService.getServiceEndpointsAsync(projectId, "artifacts-upstream");
            return endpoints;
        }

        return endpoints;
    }

    private _getControlsAccordingToStage = (): JSX.Element => {
        if (this._getDisplayOnlyPublicUpstream(this.props)) {
            return this._getAddPublicSourcePanelSection();
        }

        switch (this.state.stage) {
            default:
            case AddUpstreamPanelStage.Initial: {
                const availablePublicUpstreamSources = this._getAvailablePublicUpstreamSources();
                return (
                    <UpstreamTypeSelectionPanelSection
                        pageContext={this.props.pageContext}
                        hasAvailablePublicUpstreamSources={
                            availablePublicUpstreamSources != null && availablePublicUpstreamSources.length > 0
                        }
                        onStageChangedHandler={this._stageHandler}
                        hasAvailableFeeds={
                            this.props.availableFeedsForUpstreams && this.props.availableFeedsForUpstreams.length > 0
                        }
                        internalUpstreamSettings={this.props.internalUpstreamSettings}
                    />
                );
            }
            case AddUpstreamPanelStage.PublicUpstream: {
                return this._getAddPublicSourcePanelSection();
            }
            case AddUpstreamPanelStage.AadUpstream: {
                return (
                    <AddAadInternalSourcePanelSection
                        pageContext={this.props.pageContext}
                        selectedUpstreamSourcesHandler={this._selectedUpstreamSourcesHandler}
                        upstreamSourceNameInvalidMessage={this.props.upstreamSourceNameInvalidMessage}
                        internalUpstreamSettings={this.props.internalUpstreamSettings}
                        serviceEndpoints={this.state.serviceEndpoints}
                        isServiceEndpointUpstreamsFeatureEnabled={this.props.isServiceEndpointUpstreamsFeatureEnabled}
                        projectId={this.props.currentFeed.project?.id
                            || getCurrentSelectedProjectId(this.props.pageContext)}
                    />
                );
            }
            case AddUpstreamPanelStage.CollectionUpstream: {
                const internalUpstreamSources = this.props.activeUpstreamSources.filter(
                    (source: UpstreamSource) => source.upstreamSourceType === UpstreamSourceType.Internal
                );
                return (
                    <AddCollectionInternalSourcePanelSection
                        pageContext={this.props.pageContext}
                        availableFeedsForUpstreams={this.props.availableFeedsForUpstreams}
                        selectedUpstreamSourcesHandler={this._selectedUpstreamSourcesHandler}
                        currentInternalUpstreamSources={internalUpstreamSources}
                        upstreamSourceNameInvalidMessage={this.props.upstreamSourceNameInvalidMessage}
                        internalUpstreamSettings={this.props.internalUpstreamSettings}
                    />
                );
            }
        }
    };

    private _getAddPublicSourcePanelSection(): JSX.Element {
        const availablePublicUpstreamSources = this._getAvailablePublicUpstreamSources();
        return (
            <AddPublicSourcePanelSection
                availablePublicUpstreamSources={availablePublicUpstreamSources}
                selectedUpstreamSourcesHandler={this._selectedUpstreamSourcesHandler}
                upstreamSourceNameInvalidMessage={this.props.upstreamSourceNameInvalidMessage}
                upstreamSourceLocationInvalidMessage={this.props.upstreamSourceLocationInvalidMessage}
                isCustomPublicUpstreamsFeatureEnabled={this.props.isCustomPublicUpstreamsFeatureEnabled}
            />
        );
    }

    private _stageHandler = (stage: AddUpstreamPanelStage): void => {
        this.setState({ stage });
    };

    private _selectedUpstreamSourcesHandler = (upstreamSources: ExtendedUpstreamSource[]): void => {
        this.setState({ selectedUpstreamSources: upstreamSources });
    };

    private _onDismiss = (): void => {
        FeedSettingsActionCreator.closeAddUpstreamPanelRequested.invoke({});
    };

    private _onSave = () => {
        const newSources = this.state.selectedUpstreamSources.map(
            source => ({ ...source, deletedDate: null } as ExtendedUpstreamSource)
        );

        FeedSettingsActionCreator.addUpstreamSources.invoke(newSources);
    };

    private _onRenderFooterContent = (): JSX.Element => {
        return (
            <div className="add-upstream-panel-footer">
                <div>
                    <PrimaryButton
                        onClick={this._onSave}
                        disabled={this._shouldDisableOkFooterButton()}
                        className="add-upstream-panel-ok-button"
                    >
                        {PackageResources.AddUpstreamPanel_Save}
                    </PrimaryButton>
                    <DefaultButton onClick={this._onDismiss} disabled={this.props.isSavingChanges === true}>
                        {PackageResources.AddUpstreamPanel_Cancel}
                    </DefaultButton>
                </div>
            </div>
        );
    };

    private _shouldDisableOkFooterButton(): boolean {
        const hasInvalidMessage =
            (this.props.upstreamSourceNameInvalidMessage && this.props.upstreamSourceNameInvalidMessage !== null) ||
            (this.props.upstreamSourceLocationInvalidMessage &&
                this.props.upstreamSourceLocationInvalidMessage !== null);
        const thereAreNoSelectedUpstreamSources =
            this.state.selectedUpstreamSources == null || this.state.selectedUpstreamSources.length === 0;
        const noProtocolSelected =
            thereAreNoSelectedUpstreamSources || this.state.selectedUpstreamSources[0].protocol == null;
        const noLocationEntered =
            thereAreNoSelectedUpstreamSources ||
            (this.state.stage === AddUpstreamPanelStage.PublicUpstream &&
                (this.state.selectedUpstreamSources[0].location == null ||
                    this.state.selectedUpstreamSources[0].location === ""));
        const noNameEntered =
            thereAreNoSelectedUpstreamSources ||
            this.state.selectedUpstreamSources[0].name == null || this.state.selectedUpstreamSources[0].name === "";
        const noServiceEndpointConfigured = thereAreNoSelectedUpstreamSources ||
            (this.state.selectedUpstreamSources[0].useServiceEndpoint &&
                (this.state.selectedUpstreamSources[0].serviceEndpointId == null || this.state.selectedUpstreamSources[0].serviceEndpointId == ""));
        const upstreamSourceNotComplete = noProtocolSelected ||
            noLocationEntered ||
            noNameEntered ||
            noServiceEndpointConfigured;

        const onInitialStage = this.state.stage === AddUpstreamPanelStage.Initial;

        return hasInvalidMessage || upstreamSourceNotComplete || this.props.isSavingChanges || onInitialStage;
    }

    private _getAvailablePublicUpstreamSources(): ExtendedUpstreamSource[] {
        const availablePublicUpstreamSources = filterDefaultUpstreamSources(
            this.props.pageContext,
            this.props.currentFeed
        );

        const upstreamSourcesToRemoveMap: { [key: string]: string } = {};
        for (const upstreamSource of this.props.activeUpstreamSources) {
            upstreamSourcesToRemoveMap[upstreamSource.location] = upstreamSource.location;
        }

        const upstreamSources: ExtendedUpstreamSource[] = [];

        // remove existing upstream sources
        for (const upstreamSource of availablePublicUpstreamSources) {
            if (upstreamSourcesToRemoveMap[upstreamSource.location] == null) {
                upstreamSources.push(upstreamSource);
            }
        }

        // add Custom option
        if (this.props.isCustomPublicUpstreamsFeatureEnabled) {
            upstreamSources.push({
                deletedDate: null,
                id: null,
                internalUpstreamCollectionId: null,
                internalUpstreamFeedId: null,
                internalUpstreamViewId: null,
                location: null,
                name: PackageResources.AddUpstreamPanel_Custom,
                protocol: NpmKey, // npm option is the only one currently available
                upstreamSourceType: UpstreamSourceType.Public,
                isCustom: true
            } as ExtendedUpstreamSource);
        }

        // add new upstream sources
        return upstreamSources;
    }

    private _getDisplayOnlyPublicUpstream(props: IAddUpstreamPanelProps) {
        return props.internalUpstreamSettings.isV2Feed === false;
    }
}
